import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValue } from "framer-motion";
import { FiCheck, FiChevronDown, FiChevronLeft, FiFileText, FiImage, FiLock, FiMapPin, FiNavigation, FiPlus, FiPrinter, FiUser } from "react-icons/fi";
import PhoneFrame from "./PhoneFrame";
import Reveal from "./Reveal";
import { ORDER_NO, Sheet } from "./Printer";
import { SHOP } from "./demoData";

/*
 * "How it works", driven by scrolling: the four steps scroll past on the left while the phone
 * and a paper note on the right stay put and change with the step in view. A rail fills as you go.
 * The screens follow the app's design kit (lib/Common/pa_theme.dart).
 */

const STEPS = [
  { title: "Upload", text: "PDFs or photos from your phone or laptop. Nothing is forwarded on WhatsApp or copied to a pen drive.", facts: ["PDF, JPG, PNG", "Up to 15 files", "50 MB each"], Screen: HomeScreen },
  { title: "Set it up", text: "Choose how each file prints and check the preview before you pay. The shop prints exactly what you chose.", facts: ["Copies", "Colour or black and white", "One or both sides", "Page size"], Screen: ConfigScreen },
  { title: "Pay", text: "Pay through PhonePe. Coupons and reward points come off the bill first, and the shop gets the job only after payment.", facts: ["UPI", "Cards", "Net banking", "Coupons and rewards"], Screen: PayScreen },
  { title: "Collect", text: "The shop's printer starts the moment payment succeeds. Track it live, show your order number at the counter, walk out.", facts: ["Live tracking", "Ready alert", "Order number, no queue"], Screen: TrackScreen },
];

export default function HowItWorks() {
  const ref = useRef(null);
  const progress = useMotionValue(0);
  const [active, setActive] = useState(0);

  // progress 0 -> 1 as the step list passes a line 60% down the viewport
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let raf = 0;
    const update = () => {
      raf = 0;
      const r = el.getBoundingClientRect();
      const line = window.innerHeight * 0.6;
      const v = r.height > 0 ? Math.min(1, Math.max(0, (line - r.top) / r.height)) : 0;
      progress.set(v);
      const next = Math.min(STEPS.length - 1, Math.floor(v * STEPS.length));
      setActive((cur) => (cur === next ? cur : next));
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [progress]);
  const { Screen } = STEPS[active];

  return (
    <section className="hiw" id="how-it-works">
      <div className="wrap">
        <Reveal className="hiw__head">
          <h2>Four steps, nothing to explain at the counter</h2>
          <p>The same order flow on this site and in the app. Your settings travel with the file, so the shop prints exactly what you chose.</p>
        </Reveal>
        <div className="hiw__grid" ref={ref}>
          <ol className="hiw__steps" aria-label="Steps">
            <span className="hiw__track" aria-hidden="true">
              <motion.i className="hiw__rail" style={{ scaleY: progress }} />
            </span>
            {STEPS.map((s, i) => (
              <motion.li
                key={s.title}
                className={`hiw__step ${i === active ? "is-active" : ""} ${i < active ? "is-done" : ""}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "0px 0px -20% 0px" }}
                transition={{ duration: 0.55, ease: [0.2, 0.8, 0.2, 1] }}
              >
                <span className="hiw__num">{i < active ? <FiCheck /> : i + 1}</span>
                <div className="hiw__text">
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                  <ul className="hiw__facts">
                    {s.facts.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </div>
              </motion.li>
            ))}
          </ol>
          <div className="hiw__sticky">
            <div className="hiw__device">
              <PhoneFrame>
                <AnimatePresence initial={false}>
                  <motion.div key={active} className="app" initial={{ opacity: 0, x: 28 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -28 }} transition={{ duration: 0.28, ease: "easeOut" }}>
                    <Screen />
                  </motion.div>
                </AnimatePresence>
              </PhoneFrame>
            </div>
            <div className="hiw__paper">
              <AnimatePresence initial={false}>
                <motion.div key={active} className="note" initial={{ opacity: 0, y: 26, rotate: -3 }} animate={{ opacity: 1, y: 0, rotate: 2.5 }} exit={{ opacity: 0, y: -18, rotate: 4 }} transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}>
                  <StepNote step={active} />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/** The paper slip beside the phone: what each step leaves behind. */
function StepNote({ step }) {
  if (step === 0) {
    return (
      <>
        <small>Uploaded</small>
        <strong>assignment-3.pdf</strong>
        <span>12 pages, 1.2 MB</span>
        <i className="note__bar">
          <b style={{ width: "100%" }} />
        </i>
        <em>
          <FiCheck /> Ready to configure
        </em>
      </>
    );
  }
  if (step === 1) {
    return (
      <>
        <small>Settings</small>
        <ul className="note__list">
          <li>
            Copies <b>1</b>
          </li>
          <li>
            Sides <b>Both</b>
          </li>
          <li>
            Colour <b>Pages 3 and 7</b>
          </li>
          <li>
            Paper <b>A4</b>
          </li>
        </ul>
        <em>12 pages on 6 sheets</em>
      </>
    );
  }
  if (step === 2) {
    return (
      <>
        <small>Paid by UPI</small>
        <strong className="note__big">₹22</strong>
        <span>Order {ORDER_NO}, sent to the shop's printer</span>
        <em>
          <FiCheck /> PhonePe, secured
        </em>
      </>
    );
  }
  return (
    <>
      <small>Ready to collect</small>
      <strong>Order {ORDER_NO}</strong>
      <span>Show it at the counter of {SHOP.name}</span>
      <div className="note__sheet">
        <Sheet kind={0} />
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ app screens */

function HomeScreen() {
  return (
    <>
      <div className="app__hdr">
        <div className="app__hdr-row">
          <span className="app__brand">
            <img src="/img/print_anywhere_logo2.webp" alt="" />
            <b>Print</b>Anywhere
          </span>
          <i className="app__avatar">
            <FiUser />
          </i>
        </div>
        <small>Collect at</small>
        <strong>{SHOP.name}</strong>
        <span>
          <FiMapPin /> {SHOP.address}, {SHOP.distance}, open
        </span>
      </div>
      <div className="app__body">
        <div className="app__label-row">
          <small className="app__label">Your files</small>
          <em>Select all</em>
        </div>
        <div className="pfile">
          <span className="pfile__icon pfile__icon--pdf">
            <FiFileText />
          </span>
          <span className="pfile__body">
            <strong>assignment-3.pdf</strong>
            <small>12 pages, 1.2 MB</small>
          </span>
          <span className="pfile__check">
            <FiCheck />
          </span>
        </div>
        <div className="pfile">
          <span className="pfile__icon pfile__icon--img">
            <FiImage />
          </span>
          <span className="pfile__body">
            <strong>passport-photo.jpg</strong>
            <small>1 page, 640 KB</small>
          </span>
          <span className="pfile__check">
            <FiCheck />
          </span>
        </div>
        <div className="app__add">
          <FiPlus /> Add file
        </div>
        <div className="pscreen__cta">
          <FiPrinter /> Print 2 files
        </div>
      </div>
    </>
  );
}

function ConfigScreen() {
  return (
    <>
      <div className="app__hdr app__hdr--bar">
        <FiChevronLeft />
        <strong>assignment-3.pdf</strong>
        <em>Apply to all</em>
      </div>
      <div className="app__body">
        <div className="pscreen__preview">
          <div className="pscreen__page">
            <i />
            <i />
            <i />
            <b />
          </div>
          <div className="pscreen__page pscreen__page--grey">
            <i />
            <i />
            <i />
            <i />
          </div>
        </div>
        <div className="prow">
          <span>Copies</span>
          <span className="pstep">
            <i>−</i>
            <b>1</b>
            <i>+</i>
          </span>
        </div>
        <div className="prow prow--col">
          <span>Side</span>
          <span className="pseg pseg--two">
            <i style={{ left: "50%" }} />
            <em>Single</em>
            <em>Both sides</em>
          </span>
        </div>
        <div className="prow prow--col">
          <span>Colour</span>
          <span className="pseg pseg--four">
            <i style={{ left: "50%" }} />
            <em>B&amp;W</em>
            <em>Colour</em>
            <em>Both</em>
            <em>Custom</em>
          </span>
        </div>
        <div className="prow">
          <span>Page size</span>
          <b>
            A4 <FiChevronDown />
          </b>
        </div>
        <div className="prow">
          <span>Pages per sheet</span>
          <b>
            1 <FiChevronDown />
          </b>
        </div>
        <div className="pscreen__cta pscreen__cta--split">
          <span>
            Total <b>₹22</b>
          </span>
          <span>Done</span>
        </div>
      </div>
    </>
  );
}

function PayScreen() {
  return (
    <>
      <div className="app__hdr app__hdr--bar">
        <FiChevronLeft />
        <strong>Review your order and pay</strong>
      </div>
      <div className="app__body">
        <div className="pcard">
          <small>Collect at</small>
          <strong>{SHOP.name}</strong>
          <span>
            {SHOP.address}, {SHOP.distance}
          </span>
        </div>
        <div className="pcard">
          <small>Your files</small>
          <div className="app__file">
            <strong>assignment-3.pdf</strong>
            <span>12 pages, 1 copy, both sides, A4</span>
            <em>View details</em>
          </div>
        </div>
        <div className="pcard pcard--bill">
          <small>Bill details</small>
          <div>
            <span>Black and white, 10 pages</span>
            <b>₹10</b>
          </div>
          <div>
            <span>Colour, 2 pages</span>
            <b>₹10</b>
          </div>
          <div>
            <span>Convenience fee</span>
            <b>₹2</b>
          </div>
          <div className="is-total">
            <span>Total</span>
            <b>₹22</b>
          </div>
        </div>
        <div className="pupi">
          <small>Pay with</small>
          <span className="pupi__apps">
            <i style={{ background: "#5f259f" }}>Pe</i>
            <i style={{ background: "#1a73e8" }}>G</i>
            <i style={{ background: "#00baf2" }}>P</i>
            <i style={{ background: "#15120f" }}>UPI</i>
          </span>
        </div>
        <div className="pscreen__cta">
          <FiLock /> Pay ₹22
        </div>
      </div>
    </>
  );
}

function TrackScreen() {
  return (
    <>
      <div className="app__hdr app__hdr--bar">
        <FiChevronLeft />
        <strong>Order {ORDER_NO}</strong>
        <em className="app__live">Live</em>
      </div>
      <div className="app__body">
        <div className="pcard pcard--track">
          <div className="app__ring">
            <svg viewBox="0 0 64 64" aria-hidden="true">
              <circle cx="32" cy="32" r="27" fill="none" stroke="#ebe7e0" strokeWidth="6" />
              <circle cx="32" cy="32" r="27" fill="none" stroke="#1e9e5a" strokeWidth="6" strokeLinecap="round" />
              <path d="M20 33 l8 8 l17 -18" fill="none" stroke="#1e9e5a" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div>
              <strong>Ready to collect</strong>
              <small>Show order {ORDER_NO} at the counter</small>
            </div>
          </div>
          <span className="ptrack">
            <i style={{ width: "100%" }} />
          </span>
        </div>
        <div className="pjourney">
          <div className="is-done">
            <i>
              <FiCheck />
            </i>
            <span>Order placed</span>
          </div>
          <div className="is-done">
            <i>
              <FiCheck />
            </i>
            <span>Printing</span>
          </div>
          <div className="is-done">
            <i>
              <FiCheck />
            </i>
            <span>Ready</span>
          </div>
          <div className="is-next">
            <i>
              <FiCheck />
            </i>
            <span>Collected</span>
          </div>
        </div>
        <div className="pcard pcard--shop">
          <small>Collect at</small>
          <strong>{SHOP.name}</strong>
          <span>
            {SHOP.address}, {SHOP.distance}
          </span>
          <em>Show order {ORDER_NO} at the counter</em>
        </div>
        <div className="pscreen__cta pscreen__cta--ghost">
          <FiNavigation /> Get directions
        </div>
      </div>
    </>
  );
}
